import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { interval, map, switchMap, Subscription, takeUntil, Subject, catchError, of, mergeMap } from 'rxjs';
import { devAPI, prodAPI } from '../../../env';

export interface User {
  id?: number;
  username: string;
  type?: string;
  is_youtube?: boolean;
  is_twitch?: boolean;
  amount: number;
  keyword?: string;
}

export interface GiveawayResponse {
  state: string;
  start_time: number | null;
  keyword: string | null;
  entries: User[] | null;
  
}

export interface Alert {
  message?: {
    username: string;
    text: string;
    price: number;
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private usersSignal = signal<User[]>([]);
  private giveawayDataSignal = signal<GiveawayResponse | null>(null);
  private alertSignal = signal<Alert | null>({})
  private pollingSubscription?: Subscription;
  private alertPollingSubscription?: Subscription;
  private stopPolling$ = new Subject<void>();
  private stopAlertPolling$ = new Subject<void>();
  private giveawayStatus = signal<boolean>(true)

  constructor(private http: HttpClient) {
  }

 public startUsersPolling(intervalTime: number = 1000): void {
    if (this.pollingSubscription) return;

    this.http.get<GiveawayResponse>(`${prodAPI}/api/overlay/entries`).pipe(
      catchError((err) => {
        console.error('Initial API request failed:', err);
        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          this.giveawayDataSignal.set(response);
          if (response.state === 'idle' || !response.entries) {
            this.clearUsers();
          } else if (response.entries) {
            this.processUsers(response.entries, response.keyword);
          }
        }
      }
    });

    this.pollingSubscription = interval(intervalTime)
      .pipe(
        takeUntil(this.stopPolling$),
        mergeMap(() => 
          this.http.get<GiveawayResponse>(`${prodAPI}/api/overlay/entries`).pipe(
            catchError((err) => {
              console.error('API request failed, but polling continues:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.giveawayDataSignal.set(response);
            if (response.state === 'idle' || !response.entries) {
              this.clearUsers();
            } else if (response.entries) {
              this.processUsers(response.entries, response.keyword);
            }
          }
        },
        error: (err) => {
          console.error('Polling stream terminated with an error:', err);
        }
      });
  }

 private processUsers(users: User[], keyword?: string | null): void {
  const mappedUsers = users.map((user: User) => ({
    ...user,
    keyword: keyword || user.keyword
  }));

  // Всегда полностью перезаписываем при winners
  if (this.giveawayDataSignal()?.state === 'winners') {
    this.usersSignal.set([...mappedUsers]);
  } else {
    // Для остальных состояний - добавляем/обновляем
    this.usersSignal.update(prev => {
      const newUsersList = [...prev];
      
      for (const user of mappedUsers) {
        const existingUserIndex = newUsersList.findIndex(u => u.username === user.username);
        
        if (existingUserIndex >= 0) {
          newUsersList[existingUserIndex] = {
            ...newUsersList[existingUserIndex],
            ...user,
            id: newUsersList[existingUserIndex].id
          };
        } else {
          newUsersList.push(user);
        }
      }
      
      return newUsersList;
    });
  }
}

  public startAlertsPolling(intervalTime: number = 60000): void {
    if (this.alertPollingSubscription) return; 
    
    this.http.get<Alert>(`${prodAPI}/api/overlay/message`).pipe(
      catchError((err) => {
        console.error('Initial API request failed:', err);
        return of(null);
      })
    ).subscribe({
      next: (alert) => {
        if (alert) {
          this.alertSignal.update((prevAlert) => {
            return alert.message ? alert : prevAlert;
          });
        }
      }
    });

    this.alertPollingSubscription = interval(intervalTime)
      .pipe(
        takeUntil(this.stopAlertPolling$),
        mergeMap(() =>
          this.http.get<Alert>(`${prodAPI}/api/overlay/message`).pipe(
            catchError((err) => {
              console.error('API request failed, but polling continues:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: (alert) => {
          if (alert) {
            this.alertSignal.update((prevAlert) => {
              return alert.message ? alert : prevAlert;
            });
          }
        },
        error: (err) => {
          console.error('Polling stream terminated with an error:', err);
        }
      });
  }

  public stopUsersPolling(): void {
    this.stopPolling$.next();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  public stopAlertsPolling(): void {
    this.stopAlertPolling$.next();
    if (this.alertPollingSubscription) {
      this.alertPollingSubscription.unsubscribe();
      this.alertPollingSubscription = undefined;
    }
  }

  private clearUsers(): void {
    this.usersSignal.set([]);
  }

  getUsers() {
    return this.usersSignal.asReadonly();
  }

  getGiveawayData() {
    return this.giveawayDataSignal.asReadonly();
  }

  getAlert() {
    return this.alertSignal.asReadonly();
  }

  duplicateUsers(): void {
    this.usersSignal.update(users => {
      return [...users, ...users];
    });
  }

  resetAlert(): void {
    this.alertSignal.set(null)
  }
}